import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({ permissions: [], role: null, is_org_admin: false, invitable_roles: [], account_status: 'active' });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const [meRes, permsRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/me/permissions`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setUser(meRes.data);
      setPermissions(permsRes.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      // Fetch permissions BEFORE returning so caller's navigate has correct gates
      let perms = null;
      try {
        const p = await axios.get(`${API}/me/permissions`, { headers: { Authorization: `Bearer ${access_token}` } });
        perms = p.data;
        setPermissions(perms);
      } catch { /* fall back to default */ }
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      return { success: true, permissions: perms };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { access_token, user: newUser } = response.data;
      let perms = null;
      try {
        const p = await axios.get(`${API}/me/permissions`, { headers: { Authorization: `Bearer ${access_token}` } });
        perms = p.data;
        setPermissions(perms);
      } catch { /* fall back */ }
      setToken(access_token);
      setUser(newUser);
      localStorage.setItem('token', access_token);
      return { success: true, permissions: perms };
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? (detail[0]?.msg || 'Registration failed') : (detail || 'Registration failed');
      return { success: false, error: msg };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setPermissions({ permissions: [], role: null, is_org_admin: false, invitable_roles: [], account_status: 'active' });
    localStorage.removeItem('token');
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const hasPerm = (modId) => permissions.permissions?.includes(modId);
  const isOrgAdmin = !!permissions.is_org_admin;

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, loading, getAuthHeader, permissions, hasPerm, isOrgAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};